import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { CLOSURE_REASON_TABLE_BASE } from '../table-configs';
import { DateFormatService } from '../../../core/services/date-format.service';

/**
 * Canned reasons offered in the Close Membership dropdown. Reasons are never
 * deleted — the chosen text is stored on the closed member — so retiring one
 * means marking it Inactive, which hides it from the dropdown.
 */
@Component({
  selector: 'app-closure-reason-master',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, VioTablesV2Module],
  templateUrl: './closure-reason-master.component.html',
  styleUrl: './closure-reason-master.component.scss',
})
export class ClosureReasonMasterComponent implements OnInit {
  view: 'list' | 'form' = 'list';

  // List state
  records: any[] = [];
  isListLoading = false;

  // Form state
  form!: FormGroup;
  isEditMode = false;
  editId: string | null = null;
  isSaving = false;
  successMessage = '';
  errorMessage = '';

  tableConfig: VT2_TABLE_CONFIG = {
    ...CLOSURE_REASON_TABLE_BASE,
    actionButtons: [
      { icon: '✏', title: 'Edit', cssClass: 'vt2-icon-btn--edit', onClick: (row) => this.editRecord(row) },
    ],
  };

  constructor(private fb: FormBuilder, private api: ApiService, private pageTitle: PageTitleService, private dateFmt: DateFormatService) {}

  ngOnInit(): void {
    this.tableConfig = { ...this.tableConfig, dateDisplayFormat: this.dateFmt.current };
    this.pageTitle.set('Closure Reasons', 'Reasons offered when closing a membership');
    this.buildForm();
    this.loadList();
  }

  buildForm(): void {
    this.form = this.fb.group({
      reasonName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      isActive:   [true],
    });
  }

  loadList(): void {
    this.isListLoading = true;
    this.api.getClosureReasons().subscribe({
      next: (data) => {
        this.records = (data || []).map((r: any) => ({ ...r, activeLabel: r.isActive === false ? 'Inactive' : 'Active' }));
        this.isListLoading = false;
      },
      error: () => {
        this.records = [];
        this.isListLoading = false;
      },
    });
  }

  addNew(): void {
    this.isEditMode = false;
    this.editId = null;
    this.form.reset({ reasonName: '', isActive: true });
    this.successMessage = '';
    this.errorMessage = '';
    this.view = 'form';
    this.pageTitle.set('Closure Reasons', 'Add a new closure reason');
  }

  editRecord(record: any): void {
    this.isEditMode = true;
    this.editId = record._id || record.id;
    this.successMessage = '';
    this.errorMessage = '';
    this.form.patchValue({
      reasonName: record.reasonName,
      isActive:   record.isActive !== false,
    });
    this.view = 'form';
    this.pageTitle.set('Closure Reasons', 'Update closure reason');
  }

  backToList(): void {
    this.view = 'list';
    this.pageTitle.set('Closure Reasons', 'Reasons offered when closing a membership');
    this.loadList();
  }

  get f() { return this.form.controls; }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    this.successMessage = '';
    this.errorMessage = '';

    const request$ = this.isEditMode
      ? this.api.updateClosureReason({ ...this.form.value, id: this.editId })
      : this.api.saveClosureReason(this.form.value);

    request$.subscribe({
      next: (res: any) => {
        this.isSaving = false;
        const n = res?.membersUpdated || 0;
        this.successMessage = this.isEditMode
          ? `Closure reason updated successfully!${n ? ` ${n} closed member${n === 1 ? '' : 's'} updated to the new text.` : ''}`
          : 'Closure reason saved successfully!';
        setTimeout(() => this.backToList(), 1000);
      },
      error: (err: any) => {
        this.isSaving = false;
        this.errorMessage = err?.error?.message || err?.message || 'Failed to save closure reason.';
      },
    });
  }
}
